/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { IBM_Plex_Sans_Thai, Poppins } from "next/font/google";
import toast from "react-hot-toast";
import "./seaaii-booking.css";

const ibm = IBM_Plex_Sans_Thai({ subsets: ["thai", "latin"], weight: ["400", "500", "600", "700"], variable: "--font-ibm", display: "swap" });
const poppins = Poppins({ subsets: ["latin"], weight: ["600", "700"], variable: "--font-poppins", display: "swap" });

const AUTH_LOGO = "/seaaii/assets/img/auth-logo.png";
const CHAT_ICON = "/seaaii/assets/img/chat-icon.png";
const THAI_DOW = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const THAI_DOW_FULL = ["อาทิตย์", "จันทร์", "อังคาร", "พุธ", "พฤหัสบดี", "ศุกร์", "เสาร์"];
const THAI_MONTH = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const SLOTS = Array.from({ length: 12 }, (_, i) => `${String(9 + i).padStart(2, "0")}:00`); // 09:00–20:00

type Mode = "GOOGLE_MEET" | "PHONE" | "ONSITE";
const MODE_LABEL: Record<Mode, string> = { GOOGLE_MEET: "Google Meet", PHONE: "โทรศัพท์", ONSITE: "เข้าพบที่ออฟฟิศ Seaaii" };

const fmtThaiDate = (d: Date) => `${THAI_DOW_FULL[d.getDay()]}, ${d.getDate()} ${THAI_MONTH[d.getMonth()]} ${d.getFullYear() + 543}`;
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const CalIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="17" rx="3" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>
);
const ClockIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);
const ModeIcon = ({ mode }: { mode: Mode }) => {
  if (mode === "PHONE") return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2f6bff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h3.5l1.5 5-2 1.5a13 13 0 0 0 6 6l1.5-2 5 1.5V20a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2Z" /></svg>;
  if (mode === "ONSITE") return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2f6bff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-5.6-7-11a7 7 0 0 1 14 0c0 5.4-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>;
  return <svg width="22" height="22" viewBox="0 0 24 24"><rect x="2" y="6" width="14" height="12" rx="2.5" fill="#00ac47" /><path d="M16 10l5-3v10l-5-3z" fill="#0066da" /><path d="M2 6h14v6H2z" fill="#2684fc" /><path d="M16 10l5-3v3z" fill="#ffba00" /></svg>;
};

// Post-signup "นัดหมาย Demo" screen for Seaaii leads. Picks a date/time/mode +
// note, posts to /api/booking/demo (keyed by the freshly-created userId), then
// shows a success screen. "ข้ามไปก่อน" calls onSkip (→ the pending thank-you).
export default function SeaaiiDemoBooking({ userId, onSkip }: { userId: string; onSkip: () => void }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [viewY, setViewY] = useState(today.getFullYear());
  const [viewM, setViewM] = useState(today.getMonth());
  const [selDate, setSelDate] = useState<Date>(today);
  const [selTime, setSelTime] = useState("10:00");
  const [mode, setMode] = useState<Mode>("GOOGLE_MEET");
  const [note, setNote] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [taken, setTaken] = useState<Set<string>>(new Set());

  // Load which slots on the selected day are already booked, so they can't be
  // picked again. If the current pick just got taken, jump to the first free one.
  useEffect(() => {
    const from = new Date(selDate.getFullYear(), selDate.getMonth(), selDate.getDate(), 0, 0, 0);
    const to = new Date(selDate.getFullYear(), selDate.getMonth(), selDate.getDate() + 1, 0, 0, 0);
    let cancelled = false;
    fetch(`/api/booking/demo/availability?from=${from.toISOString()}&to=${to.toISOString()}`)
      .then((r) => r.json())
      .then((d: { taken?: string[] }) => {
        if (cancelled) return;
        const set = new Set((d.taken || []).map((iso) => `${String(new Date(iso).getHours()).padStart(2, "0")}:00`));
        setTaken(set);
        setSelTime((cur) => (set.has(cur) ? SLOTS.find((s) => !set.has(s)) ?? "" : cur));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selDate]);

  const cells = useMemo(() => {
    const first = new Date(viewY, viewM, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
    const prevDays = new Date(viewY, viewM, 0).getDate();
    const out: Array<{ d: number; mute: boolean; date?: Date }> = [];
    for (let i = startDow - 1; i >= 0; i--) out.push({ d: prevDays - i, mute: true });
    for (let d = 1; d <= daysInMonth; d++) out.push({ d, mute: false, date: new Date(viewY, viewM, d) });
    while (out.length % 7 !== 0 || out.length < 42) out.push({ d: 0, mute: true });
    return out.slice(0, 42);
  }, [viewY, viewM]);

  const prevMonth = () => { setViewM((m) => (m === 0 ? 11 : m - 1)); if (viewM === 0) setViewY((y) => y - 1); };
  const nextMonth = () => { setViewM((m) => (m === 11 ? 0 : m + 1)); if (viewM === 11) setViewY((y) => y + 1); };

  const scheduledAt = useMemo(() => {
    const [h, m] = selTime.split(":").map(Number);
    return new Date(selDate.getFullYear(), selDate.getMonth(), selDate.getDate(), h, m, 0);
  }, [selDate, selTime]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await fetch("/api/booking/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, scheduledAt: scheduledAt.toISOString(), mode, note: note.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "ไม่สามารถบันทึกนัดหมายได้");
      setModalOpen(false);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`sa-book ${ibm.variable} ${poppins.variable}`}>
      <div className="bg" aria-hidden="true">
        <div className="glow g1" />
        <div className="glow g2" />
        <svg className="waves" viewBox="0 0 820 720" fill="none" preserveAspectRatio="xMinYMax meet">
          <path d="M-40 380 C 160 270 280 500 470 450 C 610 414 660 600 820 560 L820 760 L-40 760 Z" fill="#cfe0fa" opacity=".6" />
          <path d="M-40 500 C 140 410 290 580 470 545 C 630 516 680 660 820 640 L820 760 L-40 760 Z" fill="#bcd3f6" opacity=".7" />
          <path d="M-40 600 C 170 540 280 660 480 640 C 640 624 700 700 820 690 L820 760 L-40 760 Z" fill="#aac6f1" />
        </svg>
      </div>

      <div className="shell">
        {!done && (
        <header>
          <a className="brand" href="https://seaaii.com">
            <Image src={AUTH_LOGO} alt="Seaaii" width={325} height={267} priority style={{ width: 40, height: 40, objectFit: "contain" }} />
            <span className="name">Seaaii</span>
          </a>
          <div className="top-right">
            <button type="button" className="lang">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>
              <span>ไทย</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <div className="sep" />
            <a className="site-link" href="https://seaaii.com">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
              <span>ไปยังเว็บไซต์</span>
            </a>
          </div>
        </header>
        )}

        <main>
          {done ? (
            <section className="card success-card">
              <div className="succ-check">
                <div className="ring" />
                <svg className="spark s1" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.7 4.5 2.8 6.6 9 8-6.2 1.4-8.3 3.5-9 8-.7-4.5-2.8-6.6-9-8 6.2-1.4 8.3-3.5 9-8Z" /></svg>
                <svg className="spark s2" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.7 4.5 2.8 6.6 9 8-6.2 1.4-8.3 3.5-9 8-.7-4.5-2.8-6.6-9-8 6.2-1.4 8.3-3.5 9-8Z" /></svg>
                <svg className="spark s3" width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.7 4.5 2.8 6.6 9 8-6.2 1.4-8.3 3.5-9 8-.7-4.5-2.8-6.6-9-8 6.2-1.4 8.3-3.5 9-8Z" /></svg>
                <span className="dot d1" />
                <span className="dot d2" />
                <div className="disc"><svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7" /></svg></div>
              </div>
              <h1>ลงทะเบียนสำเร็จ</h1>
              <p className="succ-sub">เราได้รับข้อมูลการนัดหมายของท่านเรียบร้อยแล้ว</p>
              <p className="succ-note">เจ้าหน้าที่จะติดต่อกลับภายใน 24 ชั่วโมง<br />เพื่อแนะนำการเริ่มต้นใช้งานและนัด Demo</p>

              <div className="succ-divider" />
              <div className="succ-contact">
                <span className="chat"><Image src={CHAT_ICON} alt="" width={30} height={30} /></span>
                หากต้องการสอบถามเพิ่มเติม สามารถติดต่อทีมงานได้ทันที
              </div>

              <div className="succ-actions">
                <button type="button" className="btn-primary" onClick={onSkip}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M11.3 3.3a1 1 0 0 1 1.4 0l8 7.5A1 1 0 0 1 20 12.5h-1V20a1 1 0 0 1-1 1h-3.5a1 1 0 0 1-1-1v-4h-3v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7.5H4a1 1 0 0 1-.7-1.7l8-7.5Z" /></svg>
                  กลับหน้าหลัก
                </button>
                <button type="button" className="btn-outline" onClick={() => setCallOpen(true)}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11a8 8 0 0 1 16 0" /><rect x="2.5" y="11" width="4" height="7" rx="2" /><rect x="17.5" y="11" width="4" height="7" rx="2" /><path d="M20 18a3 3 0 0 1-3 3h-3" /></svg>
                  ติดต่อทีมงาน
                </button>
              </div>
            </section>
          ) : (
            <section className="card">
              <div className="success-pill">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></svg>
                สมัครสมาชิกสำเร็จ
              </div>
              <div className="head">
                <Image src={AUTH_LOGO} alt="" width={325} height={267} priority style={{ width: 60, height: 60, objectFit: "contain" }} />
                <h1>นัดหมาย <span className="name-en">Demo</span></h1>
              </div>
              <p className="sub">เลือกวันและเวลาที่สะดวก เพื่อยืนยันนัดหมาย Seaaii กับทีมงานของเรา</p>

              <div className="grid3">
                {/* DATE */}
                <div>
                  <h2 className="col-title">เลือกวันที่ต้องการ</h2>
                  <div className="cal">
                    <div className="cal-top">
                      <button type="button" className="cal-nav" onClick={prevMonth}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg></button>
                      <span className="m">{THAI_MONTH[viewM]} {viewY + 543}</span>
                      <button type="button" className="cal-nav" onClick={nextMonth}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg></button>
                    </div>
                    <div className="cal-grid">
                      {THAI_DOW.map((d) => <div key={d} className="dow">{d}</div>)}
                      {cells.map((c, i) => {
                        const past = c.date ? c.date.getTime() < today.getTime() : false;
                        const muted = c.mute || past;
                        const sel = c.date && sameDay(c.date, selDate);
                        const isToday = c.date && sameDay(c.date, today);
                        return (
                          <div
                            key={i}
                            className={`day${muted ? " muted" : ""}${sel ? " sel" : ""}${!sel && isToday ? " today" : ""}`}
                            onClick={() => { if (c.date && !muted) setSelDate(c.date); }}
                          >
                            {c.d || ""}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="col-foot">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" strokeLinecap="round" /></svg>
                    เวลาที่แสดงเป็นเวลาตามประเทศไทย (GMT+7)
                  </div>
                </div>

                {/* TIME */}
                <div>
                  <h2 className="col-title">เลือกเวลาที่สะดวก</h2>
                  <div className="slots">
                    {SLOTS.map((t) => {
                      const isTaken = taken.has(t);
                      return (
                        <button type="button" key={t} disabled={isTaken} title={isTaken ? "ช่วงเวลานี้มีผู้จองแล้ว" : undefined} className={`slot${t === selTime ? " sel" : ""}`} onClick={() => setSelTime(t)}>
                          {t}
                          <span className="chk"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-11" /></svg></span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="col-foot">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    ระยะเวลา 45 นาที
                  </div>
                </div>

                {/* SUMMARY */}
                <div>
                  <div className="summary">
                    <h3>สรุปการนัดหมาย</h3>
                    <div className="sum-row"><div className="sum-ic">{CalIcon}</div><div><div className="lbl">วันและวันที่</div><div className="val">{fmtThaiDate(selDate)}</div></div></div>
                    <div className="sum-row"><div className="sum-ic">{ClockIcon}</div><div><div className="lbl">เวลา</div><div className="val">{selTime} น.</div></div></div>
                  </div>

                  <div className="field-label">รูปแบบการนัดหมาย</div>
                  <div className="selectwrap">
                    <span className="ic-lead"><ModeIcon mode={mode} /></span>
                    <select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
                      <option value="GOOGLE_MEET">Google Meet</option>
                      <option value="PHONE">โทรศัพท์</option>
                      <option value="ONSITE">เข้าพบที่ออฟฟิศ Seaaii</option>
                    </select>
                    <svg className="ic-caret" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                  </div>

                  <div className="field-label">รายละเอียดเพิ่มเติม <span style={{ fontWeight: 400, color: "var(--muted)" }}>(ไม่บังคับ)</span></div>
                  <div className="ta-wrap">
                    <textarea maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="เช่น หัวข้อที่ต้องการพูดคุย หรือคำถามล่วงหน้า" />
                    <span className="counter">{note.length} / 200</span>
                  </div>
                </div>
              </div>

              <button type="button" className="btn-primary" disabled={!selTime} onClick={() => setModalOpen(true)}>
                {CalIcon}
                ยืนยันการนัด Demo
              </button>
              <div className="divider">หรือ</div>
              <button type="button" className="skip" onClick={onSkip}>ข้ามไปก่อน</button>
            </section>
          )}
        </main>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true">
            <div className="modal-ic">{CalIcon}</div>
            <h3>ยืนยันการนัดหมาย?</h3>
            <p className="modal-sub">กรุณาตรวจสอบรายละเอียดก่อนยืนยัน</p>
            <div className="modal-box">
              <div className="modal-row"><span>วันและเวลา</span><b>{fmtThaiDate(selDate)}</b></div>
              <div className="modal-row"><span>เวลา</span><b>{selTime} น.</b></div>
              <div className="modal-row"><span>รูปแบบ</span><b>{MODE_LABEL[mode]}</b></div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)} disabled={submitting}>ยกเลิก</button>
              <button type="button" className="btn-primary" onClick={handleConfirm} disabled={submitting}>{submitting ? "กำลังบันทึก..." : "ยืนยันการนัด"}</button>
            </div>
          </div>
        </div>
      )}

      {callOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setCallOpen(false); }}>
          <div className="modal call-card" role="dialog" aria-modal="true">
            <div className="call-ic">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h3.5l1.5 5-2 1.5a13 13 0 0 0 6 6l1.5-2 5 1.5V20a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2Z" /></svg>
            </div>
            <h3>ติดต่อทีมงาน Seaaii</h3>
            <p className="call-lead">โทรหาเราได้ที่เบอร์นี้เลย</p>
            <span className="call-number">095-598-9191</span>
            <div className="call-actions">
              <a className="btn-primary" href="tel:0955989191" style={{ marginTop: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h3.5l1.5 5-2 1.5a13 13 0 0 0 6 6l1.5-2 5 1.5V20a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2Z" /></svg>
                โทรเลย
              </a>
              <button type="button" className="btn-edit" onClick={() => setCallOpen(false)}>ปิด</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
